import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock, ShieldCheck, Loader2, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { useLocalizedNavigate } from '../i18n/useLocalizedNav';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const validatePassword = (password: string) => {
    const rules = [
      { id: 'length', label: 'At least 8 characters', met: password.length >= 8 },
      { id: 'upper', label: 'One uppercase letter', met: /[A-Z]/.test(password) },
      { id: 'number', label: 'One number', met: /[0-9]/.test(password) },
      { id: 'special', label: 'One special character (!@#$%^&*)', met: /[!@#$%^&*]/.test(password) },
    ];
    return rules;
  };

  const passwordRules = validatePassword(formData.newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t('reset_err_session'));
      return;
    }

    setIsLoading(true);
    setError('');

    // Validation
    const unmetRules = passwordRules.filter(r => !r.met);
    if (unmetRules.length > 0) {
      const rules = unmetRules
        .map(r => t(`password_rule_${r.id}`).toLowerCase())
        .join(', ');
      setError(t('auth_err_password_missing', { rules }));
      setIsLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('auth_err_passwords_no_match'));
      setIsLoading(false);
      return;
    }

    try {
      const res = await api.post('/auth/reset-password', {
        email,
        otp: formData.otp,
        newPassword: formData.newPassword,
      });

      if (res.error) {
        setError(res.error);
      } else {
        navigate('/login', { state: { message: t('reset_msg_success') } });
      }
    } catch (err) {
      setError(t('reset_err_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-warm-200 shadow-sm space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 bg-brand-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="text-brand-primary w-8 h-8" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-warm-800">{t('reset_title')}</h2>
        <p className="text-warm-500 text-sm">{t('verify_sent_to')} <br /><span className="font-bold text-warm-800">{email}</span></p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider text-center block">{t('verify_code_label')}</label>
          <input 
            type="text" 
            placeholder="000000"
            maxLength={6}
            className="w-full text-center text-3xl tracking-[0.5em] py-4 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none font-mono"
            value={formData.otp}
            onChange={(e) => setFormData({...formData, otp: e.target.value.replace(/\D/g, '')})}
            required
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_new_password_label')}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full pl-10 pr-12 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.newPassword}
              onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth_hide_password') : t('auth_show_password')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-brand-accent transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {passwordRules.map(rule => (
              <div key={rule.id} className={`flex items-center space-x-2 text-[10px] font-bold uppercase transition-colors ${rule.met ? 'text-brand-success' : 'text-warm-500'}`}>
                <div className={`w-1 h-1 rounded-full ${rule.met ? 'bg-brand-success' : 'bg-warm-200'}`} />
                <span>{t(`password_rule_${rule.id}`)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_confirm_password_label')}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input 
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full pl-10 pr-12 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              required
            />
          </div>
        </div>

        {error && (
          <div className="bg-brand-danger/10 text-brand-danger p-3 rounded-xl text-xs font-bold border border-brand-danger/20 text-center">
            {error}
          </div>
        )}

        <button 
          type="submit" 
          disabled={isLoading || formData.otp.length !== 6}
          className="w-full py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all flex items-center justify-center disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('reset_submit')}
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
